import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardStats, getRealtimeAbsensi, getGuruList } from '../../lib/api';
import { formatTime, getStatusColor, getStatusLabel, getRoleLabel } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import ProtectedImage from '../ui/ProtectedImage';
import { 
  Users, UserCheck, UserX, Clock, AlertTriangle,
  TrendingUp, FileText, MapPin, RefreshCw, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const KepsekDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  const [guruList, setGuruList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // Auto refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [statsData, realtime, guru] = await Promise.all([
        getDashboardStats().catch(() => null),
        getRealtimeAbsensi().catch(() => null),
        // Include kepala_sekolah and tenaga_tata_usaha in the list
        getGuruList(null, true, true).catch(() => [])
      ]);
      if (statsData) setStats(statsData);
      if (realtime) setRealtimeData(realtime);
      setGuruList(guru || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="kepsek-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-indigo-950">Dashboard Kepala Sekolah</h1>
          <p className="text-gray-500">{user?.nama}</p>
        </div>
        <Button variant="outline" onClick={loadData} className="border-indigo-200">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-950">{stats?.total_guru || 0}</p>
                <p className="text-xs text-gray-500">Total Guru</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-950">{stats?.sudah_absen || 0}</p>
                <p className="text-xs text-gray-500">Sudah Absen</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <UserX className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-950">{stats?.belum_absen || 0}</p>
                <p className="text-xs text-gray-500">Belum Absen</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-950">{stats?.izin_pending || 0}</p>
                <p className="text-xs text-gray-500">Izin Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Today's Status Breakdown */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card className="border-indigo-100/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Status Kehadiran Hari Ini
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                <span className="text-emerald-800">Hadir Tepat Waktu</span>
                <span className="text-xl font-bold text-emerald-600">{stats?.hadir_tepat_waktu || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <span className="text-amber-800">Hadir Terlambat</span>
                <span className="text-xl font-bold text-amber-600">{stats?.hadir_terlambat || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <span className="text-red-800">Belum Absen</span>
                <span className="text-xl font-bold text-red-600">{stats?.belum_absen || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Attendance */}
        <Card className="border-indigo-100/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Absensi Terbaru
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-64 overflow-auto">
              {realtimeData?.absensi_terbaru?.length > 0 ? (
                realtimeData.absensi_terbaru.map((absen) => (
                  <div key={absen.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                      {absen.foto_url && (
                        <ProtectedImage src={absen.foto_url} alt="" className="w-full h-full" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{absen.guru_nama}</p>
                      <p className="text-xs text-gray-500">{formatTime(absen.waktu_absen)}</p>
                    </div>
                    <Badge className={`text-xs ${getStatusColor(absen.status)}`}>
                      {getStatusLabel(absen.status).split(' ')[0]}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-8">Belum ada absensi hari ini</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Guru List */}
      <Card className="border-indigo-100/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Daftar Pegawai
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>NIP/NUPTK</th>
                  <th>Nama</th>
                  <th>Jabatan</th>
                  <th>Status Hari Ini</th>
                </tr>
              </thead>
              <tbody>
                {guruList.map((guru) => {
                  // Use guru_status_hari_ini map for accurate status matching
                  const todayStatus = realtimeData?.guru_status_hari_ini?.[guru.id];
                  const getRoleLabel = (role) => {
                    const labels = {
                      'guru': 'Guru',
                      'kepala_sekolah': 'Kepala Sekolah',
                      'tenaga_tata_usaha': 'Tenaga Tata Usaha',
                    };
                    return labels[role] || role;
                  };
                  return (
                    <tr key={guru.id}>
                      <td className="font-mono text-sm">{guru.nip || guru.nuptk || '-'}</td>
                      <td>{guru.nama}</td>
                      <td className="text-gray-600">{guru.jabatan || getRoleLabel(guru.role)}</td>
                      <td>
                        {todayStatus ? (
                          <Badge className={getStatusColor(todayStatus.status)}>
                            {getStatusLabel(todayStatus.status)}
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600">Belum Absen</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/laporan')}
          data-testid="laporan-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          Laporan Kinerja
        </Button>
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/peta')}
          data-testid="peta-btn"
        >
          <MapPin className="w-4 h-4 mr-2" />
          Peta Absensi
        </Button>
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/izin')}
          data-testid="kelola-izin-btn"
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Kelola Izin
        </Button>
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/guru')}
          data-testid="kelola-guru-btn"
        >
          <Users className="w-4 h-4 mr-2" />
          Kelola Guru
        </Button>
      </div>
    </div>
  );
};

export default KepsekDashboard;
